import { nullSafeGet } from './DataAccessUtils';
import { ROLE_TYPES } from './DataConstants';

function escapeForMapPopupText(value) {
	if (value == null || value === '') {
		return '';
	}
	return String(value)
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;');
}

export interface LocationsMapPopupParts {
	titleHtml: string;
	bodyHtml: string;
}

/**
 * Contractor locations use flat `primaryAddress.*` and `longitude`/`latitude` (not nested under `buyerFacility`).
 */
export function buildContractorLocationsMapPopupParts(record): LocationsMapPopupParts {
	const street = nullSafeGet('primaryAddress.streetAddress1', record);
	const cityLine = [
		nullSafeGet('primaryAddress.city', record),
		nullSafeGet('primaryAddress.region', record),
		nullSafeGet('primaryAddress.postcode', record),
	]
		.filter(Boolean)
		.join(' ');

	const rawId = nullSafeGet('id', record);
	const safeId = escapeForMapPopupText(rawId == null ? '' : String(rawId));
	const detailHref = `/supplier/contractor_clients/locations/detail/${safeId}/details`;

	const titleHtml = `<div class="h5medium text-character-primary leading-snug">${escapeForMapPopupText(
		record.name
	)}</div>`;

	const bodyHtml = `
				${
					street
						? `<div class="bodyregular text-character-primary mb-0.5 leading-snug">${escapeForMapPopupText(
								street
						  )}</div>`
						: ''
				}
				${
					cityLine
						? `<div class="bodyregular text-character-secondary mb-0.5 leading-snug">${escapeForMapPopupText(
								cityLine
						  )}</div>`
						: ''
				}
				<a class="bodymedium text-supplier mt-2.5 inline-block no-underline hover:underline" href="${detailHref}">View location</a>
			`;

	return { titleHtml, bodyHtml };
}

export function buildLocationsMapPopupParts(record, roleType): LocationsMapPopupParts {
	const street = nullSafeGet('buyerFacility.primaryAddress.streetAddress1', record);
	const cityLine = [
		nullSafeGet('buyerFacility.primaryAddress.city', record),
		nullSafeGet('buyerFacility.primaryAddress.region', record),
		nullSafeGet('buyerFacility.primaryAddress.postcode', record),
	]
		.filter(Boolean)
		.join(' ');
	const woCountRaw = nullSafeGet('totalNotClosedWOCount', record);
	const woSummaryBlock =
		typeof woCountRaw === 'number' && !Number.isNaN(woCountRaw)
			? `<div class="footnoteregular text-character-secondary mt-2">${escapeForMapPopupText(
					woCountRaw > 0 ? `${woCountRaw} open work orders` : 'No open work orders'
			  )}</div>`
			: '';
	const isSupplier = roleType === ROLE_TYPES.SUPPLIER;
	const rawId = nullSafeGet('id', record);
	const safeId = escapeForMapPopupText(rawId == null ? '' : String(rawId));
	const detailHref = `${isSupplier ? '/supplier' : '/buyer'}/locations/detail/${safeId}`;
	const linkColorClass = isSupplier ? 'text-supplier' : 'text-buyer';

	const titleHtml = `<div class="h5medium text-character-primary leading-snug">${escapeForMapPopupText(
		record.name
	)}</div>`;

	const bodyHtml = `
				${
					street
						? `<div class="bodyregular text-character-primary mb-0.5 leading-snug">${escapeForMapPopupText(
								street
						  )}</div>`
						: ''
				}
				${
					cityLine
						? `<div class="bodyregular text-character-secondary mb-0.5 leading-snug">${escapeForMapPopupText(
								cityLine
						  )}</div>`
						: ''
				}
				${woSummaryBlock}
				<a class="bodymedium ${linkColorClass} mt-2.5 inline-block no-underline hover:underline" href="${detailHref}">View location</a>
			`;

	return { titleHtml, bodyHtml };
}
